import { API_BASE_URL } from '../Variables';
import { listingCoordinates } from '../utils/listingMapData';
import { retryAfterSeconds } from '../utils/retryAfter';

export const LINA_AI_ENABLED = process.env.REACT_APP_LINA_AI_ENABLED === 'true';
const uuid = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;
const text = (value, max = 500) => typeof value === 'string' ? value.slice(0, max) : '';
const number = value => typeof value === 'number' && Number.isFinite(value) ? value : null;
const positive = value => number(value) > 0 ? value : null;
const date = value => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : '';
const photo = value => {
  if (typeof value !== 'string' || value.length > 2048) return '';
  try { const url = new URL(value); return url.protocol === 'https:' ? url.href : ''; } catch { return ''; }
};
function listing(item) {
  if (!item || !uuid.test(item.id)) return null;
  const latitude = number(item.latitude), longitude = number(item.longitude);
  return {
    id: item.id, type: ['sale', 'rent', 'commercial', 'vacation'].includes(item.type) ? item.type : '',
    category: text(item.category, 80), city: text(item.city, 80), delegation: text(item.delegation, 80),
    price: positive(item.price), surface: positive(item.surface), imageUrl: photo(item.imageUrl),
    latitude: latitude !== null && Math.abs(latitude) <= 90 && longitude !== null && Math.abs(longitude) <= 180 ? latitude : null,
    longitude: latitude !== null && Math.abs(latitude) <= 90 && longitude !== null && Math.abs(longitude) <= 180 ? longitude : null,
  };
}
const items = value => Array.isArray(value) ? value.slice(0, 20).map(listing).filter(Boolean) : [];
const criteriaKeys = ['type', 'city', 'delegation', 'category', 'minPrice', 'maxPrice', 'minSurface', 'maxSurface', 'bedrooms', 'travelers', 'page', 'arrival', 'departure'];
export function normalizeLinaAiResponse(body) {
  if (!body || typeof body !== 'object' || Array.isArray(body)) throw new Error('invalid_response');
  const criteria = {};
  for (const key of criteriaKeys) {
    const value = body.criteria?.[key];
    if (typeof value === 'string') criteria[key] = text(value, 80);
    else if (number(value) !== null) criteria[key] = value;
  }
  const price = body.priceCalculation;
  const priceCalculation = price && uuid.test(price.id) && number(price.total) !== null && price.total >= 0 ? {
    id: price.id, total: price.total, nights: positive(price.nights), nightlyPrice: positive(price.nightlyPrice),
    discount: number(price.discount) !== null && price.discount >= 0 && price.discount <= 100 ? price.discount : null,
    arrival: date(price.arrival) || date(criteria.arrival), departure: date(price.departure) || date(criteria.departure),
  } : null;
  return {
    message: text(body.message, 3000), clarification: text(body.clarification, 200), criteria,
    sessionId: typeof body.sessionId === 'string' && /^[0-9a-f]{32}$/i.test(body.sessionId) ? body.sessionId : null,
    listings: items(body.listings), vacations: items(body.vacations), comparison: items(body.comparison).slice(0, 4), priceCalculation,
  };
}

// Future map integration can consume these validated coordinates; no map state is changed here.
export const linaAiMapItems = response => [...response.listings, ...response.vacations].filter(item => listingCoordinates(item));

export async function sendLinaAiMessage({ message, sessionId, language, signal }) {
  const content = message.trim();
  if (!content || content.length > 1000) throw new Error('invalid_request');
  const payload = { message: content, language: ['fr', 'en', 'ar', 'ar-TN'].includes(language) ? language : 'fr' };
  if (sessionId && /^[0-9a-f]{32}$/i.test(sessionId)) payload.sessionId = sessionId;
  const response = await fetch(`${API_BASE_URL}ai/chat`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), signal,
  });
  const raw = await response.text();
  if (raw.length > 65536) throw new Error('invalid_response');
  let body;
  try { body = JSON.parse(raw); } catch { throw new Error('invalid_response'); }
  if (!response.ok) {
    const known = ['lina_ai_disabled', 'session_expired', 'lina_ai_timeout', 'lina_ai_busy', 'provider_not_configured', 'rate_limited'];
    const error = new Error(known.includes(body.error) ? body.error : 'api_error');
    // Per-IP limit: the wait announced by the API (readable only if CORS exposes Retry-After).
    const retryAfter = response.status === 429 ? retryAfterSeconds(response.headers?.get('Retry-After')) : null;
    if (retryAfter) error.retryAfter = retryAfter;
    throw error;
  }
  return normalizeLinaAiResponse(body);
}
